/**
 * CommunicationCenterOverviewPage.tsx
 *
 * The page the Communication Center nav parent opens (/messages/overview), laid
 * out like DashboardPage: two count tiles, a "Needs attention" list of unread
 * conversations and a by-facility breakdown. Every count and row opens the
 * message list, filtered to what was clicked.
 *
 * Scoped to the global facility picker the same way MessagesPage is. A facility
 * row opens `/messages?facilityId=<id>`, which narrows the list page alone and
 * leaves the picker as it was.
 *
 * Both queries sit under the ['navigation', 'badges'] prefix, so everything
 * that refreshes the nav counts (a mention cleared, a message archived or read)
 * refreshes this page too.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AtSign, ChevronRight, Mail } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Alert } from '@/components/ui/Alert';
import { MetricCardGrid, StatCard } from '@/components/ui';
import { MENTIONS_NAV_HREF } from '@/components/layout/navBadges';
import { useEffectiveFacilityIds } from '@/hooks/useGlobalFacility';
import { facilityScopeKey } from '@/lib/facilityScopeParams';
import { useAuthStore } from '@/store/authStore';
import {
  getCommunicationOverview,
  getMessageInbox,
  type CommLogEntry,
  type CommunicationOverviewFacility,
} from '@/services/communications.service';
import { relativeTime } from '@/pages/inbox/inboxShared';
import { patientName } from './messageNames';

/** How many unread conversations "Needs attention" lists before "View all". */
const NEEDS_ATTENTION_LIMIT = 5;

/** Opens this conversation in the message list. A row with no patient opens by message alone. */
function conversationHref(entry: Pick<CommLogEntry, 'patientId' | 'commLogId'>): string {
  const params = new URLSearchParams();
  if (entry.patientId) params.set('patientId', String(entry.patientId));
  params.set('commLogId', String(entry.commLogId));
  return `/messages?${params.toString()}`;
}

function facilityLabel(row: CommunicationOverviewFacility): string {
  if (row.facilityId == null) return 'No facility';
  return row.facilityName?.trim() || 'Unnamed facility';
}

function countLabel(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** A count that opens the filtered list, or plain text when there is nothing to open. */
function CountCell({ count, to, label }: { count: number; to: string | null; label: string }) {
  if (!to || count === 0) {
    return <span className="text-muted-foreground tabular-nums">{count}</span>;
  }
  return (
    <Link to={to} aria-label={label} className="font-medium text-primary tabular-nums hover:underline">
      {count}
    </Link>
  );
}

function NeedsAttentionRow({ entry }: { entry: CommLogEntry }) {
  const name = patientName(entry);
  const facility = entry.facilityName?.trim();
  return (
    <Link
      to={conversationHref(entry)}
      className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/30"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{name}</p>
        {facility && <p className="mt-0.5 truncate text-xs text-muted-foreground">{facility}</p>}
      </div>
      <span className="flex-shrink-0 text-xs text-muted-foreground">{relativeTime(entry.createdAt)}</span>
    </Link>
  );
}

export function CommunicationCenterOverviewPage() {
  const navigate = useNavigate();
  const accountId = useAuthStore((s) => s.accountId);

  // Every facility selected in the global picker, as on MessagesPage. The key
  // keeps the numeric list stable across renders.
  const effectiveFacilityIds = useEffectiveFacilityIds();
  const facilityKey = facilityScopeKey(effectiveFacilityIds.map(Number));
  const facilityIds = useMemo(
    () => (facilityKey ? facilityKey.split(',').map(Number) : []),
    [facilityKey],
  );

  const overviewQuery = useQuery({
    queryKey: ['navigation', 'badges', 'cc-overview', accountId, facilityKey],
    queryFn: () => getCommunicationOverview(facilityIds),
    retry: false,
  });

  const needsAttentionQuery = useQuery({
    queryKey: ['navigation', 'badges', 'cc-needs-attention', accountId, facilityKey],
    queryFn: () => getMessageInbox(
      { folder: 'unread', facilityIds },
      { page: 1, pageSize: NEEDS_ATTENTION_LIMIT, includeTotal: false },
    ),
    retry: false,
  });

  if (overviewQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" label="Loading Communication Center..." />
      </div>
    );
  }

  if (overviewQuery.isError || !overviewQuery.data) {
    return (
      <div className="p-6">
        <Alert variant="error">
          Couldn't load the Communication Center summary. Refresh the page to try again.
        </Alert>
      </div>
    );
  }

  const overview = overviewQuery.data;
  const conversations = (needsAttentionQuery.data?.data ?? []).slice(0, NEEDS_ATTENTION_LIMIT);

  return (
    <div className="flex flex-col h-full overflow-hidden max-md:h-auto max-md:overflow-visible">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-border px-6 py-4 flex items-center justify-between flex-shrink-0 max-md:flex-col max-md:items-start max-md:gap-3 max-md:px-4">
        <div>
          <h1 className="text-xl font-bold text-foreground tracking-tight">Communication Center</h1>
        </div>
      </div>

      {/* ── Main scrollable content ─────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-5 max-md:px-4 max-md:py-4">
          <MetricCardGrid columns={2} className="mb-5">
            <StatCard
              label="Unread conversations"
              value={overview.unreadMessages}
              tone="blue"
              icon={<Mail className="h-4 w-4" />}
              onClick={() => navigate('/messages')}
            />
            <StatCard
              label="Mentions"
              value={overview.mentions}
              tone="violet"
              icon={<AtSign className="h-4 w-4" />}
              onClick={() => navigate(MENTIONS_NAV_HREF)}
            />
          </MetricCardGrid>

          {/* Two-up from lg only, as on DashboardPage: below 1024 the global
              sidebar is still in flow and a half-width card is too narrow. */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

            {/* ── Needs attention ─────────────────────────────────────────── */}
            <div className="lg:col-span-7">
              <Card className="border-border/80 shadow-sm h-full" data-overview-card="">
                <CardHeader className="pb-3 pt-4 px-5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">Needs attention</span>
                    <Button variant="ghost" size="sm" className="text-xs text-primary h-7 px-2 gap-1" onClick={() => navigate('/messages')}>
                      View all <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </CardHeader>
                <CardBody className="px-0 pb-0">
                  <div className="divide-y divide-border">
                    {needsAttentionQuery.isLoading ? (
                      <div className="flex justify-center px-5 py-8">
                        <Spinner size="sm" label="Loading unread conversations..." />
                      </div>
                    ) : needsAttentionQuery.isError ? (
                      <p className="px-5 py-8 text-center text-sm text-muted-foreground">
                        Couldn't load unread conversations. Refresh the page to try again.
                      </p>
                    ) : conversations.length === 0 ? (
                      <p className="px-5 py-8 text-center text-sm text-muted-foreground">Nothing needs attention right now.</p>
                    ) : (
                      conversations.map((entry) => <NeedsAttentionRow key={entry.commLogId} entry={entry} />)
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* ── By facility ─────────────────────────────────────────────── */}
            <div className="lg:col-span-5">
              <Card className="border-border/80 shadow-sm h-full" data-overview-card="">
                <CardHeader className="pb-3 pt-4 px-5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">By facility</span>
                  </div>
                </CardHeader>
                <CardBody className="px-0 pb-0">
                  {overview.byFacility.length === 0 ? (
                    <p className="px-5 py-8 text-center text-sm text-muted-foreground">
                      No unread conversations or mentions at your facilities.
                    </p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-xs text-muted-foreground">
                          <th scope="col" className="px-5 py-2 text-left font-medium">Facility</th>
                          <th scope="col" className="px-3 py-2 text-right font-medium">Unread conversations</th>
                          <th scope="col" className="px-5 py-2 text-right font-medium">Mentions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {overview.byFacility.map((row) => {
                          const label = facilityLabel(row);
                          const id = row.facilityId;
                          return (
                            <tr key={id ?? 'none'} data-facility-row="">
                              <td className="px-5 py-3 text-foreground">{label}</td>
                              <td className="px-3 py-3 text-right">
                                <CountCell
                                  count={row.unreadMessages}
                                  to={id != null ? `/messages?facilityId=${id}` : null}
                                  label={`${countLabel(row.unreadMessages, 'unread conversation', 'unread conversations')} at ${label}`}
                                />
                              </td>
                              <td className="px-5 py-3 text-right">
                                <CountCell
                                  count={row.mentions}
                                  to={id != null ? `/messages?view=mentions&facilityId=${id}` : null}
                                  label={`${countLabel(row.mentions, 'mention', 'mentions')} at ${label}`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommunicationCenterOverviewPage;
